import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../api/apiService';
import type { Exercise, Category } from '../../types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useToast } from '../../contexts/ToastContext';
import './CoachCategories.css';

export const CoachCategories: React.FC = () => {
  const toast = useToast();
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // View category exercises modal state
  const [showCategoryExercisesModal, setShowCategoryExercisesModal] = useState(false);
  const [viewingCategory, setViewingCategory] = useState<Category | null>(null);

  // Category modal state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', phases: 'all' });

  const { t } = useTranslation();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [exercisesData, categoriesData] = await Promise.all([
        apiService.getExercises(),
        apiService.getCategories(),
      ]);
      setExercises(exercisesData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(t('error'));
    } finally {
      setIsLoading(false);
    }
  };

  // Category handlers
  const handleCreateCategory = () => {
    setEditingCategory(null);
    setCategoryForm({ name: '', phases: 'all' });
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setCategoryForm({ name: category.name, phases: category.phases || 'all' });
    setShowCategoryModal(true);
  };

  const handleSaveCategory = async () => {
    try {
      if (!categoryForm.name.trim()) {
        toast.error('El nombre de la categoría es requerido');
        return;
      }

      if (editingCategory) {
        await apiService.updateCategory(editingCategory.id, categoryForm);
        toast.success(t('categoryUpdated'));
      } else {
        await apiService.createCategory(categoryForm);
        toast.success(t('categoryCreated'));
      }

      setShowCategoryModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(t('error'));
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (!window.confirm(t('confirmDelete'))) {
      return;
    }

    try {
      await apiService.deleteCategory(id);
      toast.success(t('categoryDeleted'));
      loadData();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(t('error'));
    }
  };

  const getCategoryId = (exercise: Exercise): number => {
    return exercise.category_id || exercise.category?.id || 0;
  };

  const handleViewCategoryExercises = (category: Category) => {
    setViewingCategory(category);
    setShowCategoryExercisesModal(true);
  };

  const getCategoryExercises = (categoryId: number): Exercise[] => {
    return exercises.filter((e) => getCategoryId(e) === categoryId);
  };

  const getPhaseLabels = (phases: string): string[] => {
    if (phases === 'all') {
      return [t('allPhases')];
    }
    const phaseArray = phases.split(',').map(p => p.trim());
    return phaseArray.map(phase => {
      switch(phase) {
        case 'warmup': return t('warmup');
        case 'main': return t('mainPart');
        case 'cooldown': return t('cooldown');
        default: return phase;
      }
    });
  };

  if (isLoading) {
    return <div className="text-center p-4">{t('loading')}</div>;
  }

  return (
    <div className="coach-categories-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('categories')}</h1>
          <p className="page-subtitle">Gestiona las categorías de ejercicios</p>
        </div>
        <Button onClick={handleCreateCategory} variant="primary">
          + {t('category')}
        </Button>
      </div>

      <div className="categories-grid">
        {categories.length === 0 ? (
          <Card>
            <p className="text-center">No hay categorías disponibles</p>
          </Card>
        ) : (
          categories.map((category) => (
            <Card key={category.id} className="category-card">
              <div className="category-content">
                <h3 className="category-name">{category.name}</h3>
                <p className="category-exercises-count">
                  {getCategoryExercises(category.id).length} {t('exercises').toLowerCase()}
                </p>
                <div className="category-phases-section">
                  <span className="phases-label">{t('availableIn')}:</span>
                  <div className="category-phases-badges">
                    {getPhaseLabels(category.phases || 'all').map((phaseLabel, idx) => (
                      <span key={idx} className="phase-badge">
                        {phaseLabel}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="category-actions">
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => handleViewCategoryExercises(category)}
                >
                  {t('viewExercises')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleEditCategory(category)}
                >
                  {t('edit')}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDeleteCategory(category.id)}
                >
                  {t('delete')}
                </Button>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingCategory ? t('edit') : t('create')} {t('category')}</h2>
            <div className="modal-form">
              <Input
                label={t('categoryName')}
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="Nombre de la categoría"
              />

              <div style={{ marginTop: '1rem' }}>
                <label style={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#374151',
                  display: 'block',
                  marginBottom: '0.5rem'
                }}>
                  {t('categoryPhases')}
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={categoryForm.phases === 'all'}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCategoryForm({ ...categoryForm, phases: 'all' });
                        }
                      }}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span>{t('allPhases')}</span>
                  </label>

                  {categoryForm.phases !== 'all' && (
                    <>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={categoryForm.phases.includes('warmup')}
                          onChange={(e) => {
                            const phases = categoryForm.phases.split(',').filter(p => p);
                            if (e.target.checked) {
                              phases.push('warmup');
                            } else {
                              const index = phases.indexOf('warmup');
                              if (index > -1) phases.splice(index, 1);
                            }
                            setCategoryForm({ ...categoryForm, phases: phases.length ? phases.join(',') : 'all' });
                          }}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <span>{t('warmup')}</span>
                      </label>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={categoryForm.phases.includes('main')}
                          onChange={(e) => {
                            const phases = categoryForm.phases.split(',').filter(p => p);
                            if (e.target.checked) {
                              phases.push('main');
                            } else {
                              const index = phases.indexOf('main');
                              if (index > -1) phases.splice(index, 1);
                            }
                            setCategoryForm({ ...categoryForm, phases: phases.length ? phases.join(',') : 'all' });
                          }}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <span>{t('mainPart')}</span>
                      </label>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={categoryForm.phases.includes('cooldown')}
                          onChange={(e) => {
                            const phases = categoryForm.phases.split(',').filter(p => p);
                            if (e.target.checked) {
                              phases.push('cooldown');
                            } else {
                              const index = phases.indexOf('cooldown');
                              if (index > -1) phases.splice(index, 1);
                            }
                            setCategoryForm({ ...categoryForm, phases: phases.length ? phases.join(',') : 'all' });
                          }}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <span>{t('cooldown')}</span>
                      </label>
                    </>
                  )}

                  {categoryForm.phases === 'all' && (
                    <button
                      type="button"
                      onClick={() => setCategoryForm({ ...categoryForm, phases: '' })}
                      style={{
                        marginTop: '0.5rem',
                        padding: '0.5rem',
                        fontSize: '0.875rem',
                        color: 'var(--primary)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                    >
                      Seleccionar fases específicas
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowCategoryModal(false)}>
                {t('cancel')}
              </Button>
              <Button variant="primary" onClick={handleSaveCategory}>
                {t('save')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* View Category Exercises Modal */}
      {showCategoryExercisesModal && viewingCategory && (
        <div className="modal-overlay" onClick={() => setShowCategoryExercisesModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('exercises')} - {viewingCategory.name}</h2>
            <div className="exercises-list-modal">
              {getCategoryExercises(viewingCategory.id).length === 0 ? (
                <p className="text-center">No hay ejercicios en esta categoría</p>
              ) : (
                <ul className="exercise-list">
                  {getCategoryExercises(viewingCategory.id).map((exercise) => (
                    <li key={exercise.id} className="exercise-list-item">
                      {exercise.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="modal-actions">
              <Button variant="primary" onClick={() => setShowCategoryExercisesModal(false)}>
                {t('close')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
